import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';
import { useAuth } from '@medical/shared/contexts/AuthContext';
import HospitalLogo from '@medical/shared/components/HospitalLogo';
import { hasFeature } from '@medical/shared/utils/features';
import { useTranslation } from 'react-i18next';
import { translateName, translateNameSync } from '@medical/shared/utils/translateName';

const SidebarContainer = styled.nav`
  width: 280px;
  background: #0F1F4D;
  color: white;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  z-index: 100;

  @media (max-width: 768px) {
    width: 260px;
    transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s ease;
  }
`;

const SidebarHeader = styled.div`
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
  color: white;
  font-weight: 700;
  font-size: 1.5rem;

  &:hover {
    color: #f3f4f6;
  }
`;

const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 0;
`;

const NavLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0 1rem;
`;

const NavLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 1rem 1.25rem;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1rem;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
    transform: translateX(4px);
  }

  ${props => props.active && `
    background-color: rgba(255, 255, 255, 0.25);
    font-weight: 600;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 4px;
      height: 60%;
      background-color: white;
      border-radius: 0 4px 4px 0;
    }
  `}
`;

const MedicationBadge = styled.span`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background: #ef4444;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 50%;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  padding: 0 4px;
  animation: pulse 2s infinite;

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`;

const NavLinkIcon = styled.span`
  font-size: 1.25rem;
  width: 24px;
  text-align: center;
`;

const UserSection = styled.div`
  padding: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
`;

const LogoutButton = styled.button`
  width: 100%;
  background: rgba(239, 68, 68, 0.2);
  color: white;
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;

  &:hover {
    background: rgba(239, 68, 68, 0.3);
    border-color: rgba(239, 68, 68, 0.6);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const UserProfile = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  text-decoration: none;
  color: white;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const UserAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
`;

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.25rem;
`;

const UserLabel = styled.div`
  font-size: 0.75rem;
  opacity: 0.9;
`;

const MobileMenuButton = styled.button`
  display: none;
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 200;
  background: #0B63C5;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 1.25rem;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  @media (max-width: 768px) {
    display: block;
  }
`;

const Overlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;

  @media (max-width: 768px) {
    display: ${props => props.show ? 'block' : 'none'};
  }
`;

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { t, i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadMedicationCount, setUnreadMedicationCount] = useState(0);
  const [translatedFirstName, setTranslatedFirstName] = useState('');
  const [translatedLastName, setTranslatedLastName] = useState('');

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const getInitials = () => {
    const first = user?.firstName?.[0] || '';
    const last = user?.lastName?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
    setSidebarOpen(false);
  };

  // Translate names when user or language changes
  useEffect(() => {
    const translateNames = async () => {
      if (user?.firstName) {
        const first = translateNameSync(user.firstName);
        setTranslatedFirstName(first);
        // Also try async translation for better results
        translateName(user.firstName).then(setTranslatedFirstName);
      } else {
        setTranslatedFirstName('');
      }
      
      if (user?.lastName) {
        const last = translateNameSync(user.lastName);
        setTranslatedLastName(last);
        // Also try async translation for better results
        translateName(user.lastName).then(setTranslatedLastName);
      } else {
        setTranslatedLastName('');
      }
    };

    translateNames();
  }, [user?.firstName, user?.lastName, i18n.language]);

  useEffect(() => {
    if (user && user.role === 'patient' && token) {
      fetchUnreadMedicationCount();
      // Poll for updates every 30 seconds
      const interval = setInterval(fetchUnreadMedicationCount, 30000);
      
      // Listen for refresh events from Medication page
      const handleRefresh = () => {
        fetchUnreadMedicationCount();
      };
      window.addEventListener('refreshUnreadCount', handleRefresh);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('refreshUnreadCount', handleRefresh);
      };
    }
  }, [user, token]);

  const fetchUnreadMedicationCount = async () => {
    if (!user || user.role !== 'patient' || !token) return;

    try {
      const response = await axios.get('/api/messages/unread-medication-count', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setUnreadMedicationCount(response.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching unread medication count:', error);
    }
  };

  const sidebarLabels = t('dashboard.sidebar', {
    returnObjects: true,
    defaultValue: {
      title: 'Pain Tracker',
      links: {
        dashboard: 'Dashboard',
        bodyDiagram: 'Body Diagram',
        painLogs: 'Pain Logs',
        history: 'History',
        statistics: 'Statistics',
        distress: 'Distress',
        medication: 'Medication',
      },
      user: {
        profile: 'Patient Profile',
      },
      actions: {
        logout: 'Logout',
      },
    },
  });

  return (
    <>
      <MobileMenuButton onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '✕' : '☰'}
      </MobileMenuButton>
      <Overlay show={sidebarOpen} onClick={() => setSidebarOpen(false)} />
      
      <SidebarContainer isOpen={sidebarOpen}>
        <SidebarHeader>
          <Logo to="/body-diagram">
            <img src="/logo-wordmark.jpg" alt="Anuvedana" style={{ width: '100%', maxWidth: 140, height: 'auto', borderRadius: 12 }} />
          </Logo>
          <HospitalLogo logoUrl={user?.hospitalLogoUrl} name={user?.hospitalName} size={48} stacked style={{ marginTop: '0.4rem' }} />
        </SidebarHeader>

        <SidebarContent>
          <NavLinks>
            <NavLink to="/dashboard" active={isActive('/dashboard')} onClick={() => setSidebarOpen(false)}>
              <NavLinkIcon>📊</NavLinkIcon>
              {sidebarLabels.links.dashboard}
            </NavLink>
            <NavLink to="/body-diagram" active={isActive('/body-diagram')} onClick={() => setSidebarOpen(false)}>
              <NavLinkIcon>🎯</NavLinkIcon>
              {sidebarLabels.links.bodyDiagram}
            </NavLink>
            <NavLink to="/pain-logs" active={isActive('/pain-logs')} onClick={() => setSidebarOpen(false)}>
              <NavLinkIcon>📋</NavLinkIcon>
              {sidebarLabels.links.painLogs}
            </NavLink>
            <NavLink to="/history" active={isActive('/history')} onClick={() => setSidebarOpen(false)}>
              <NavLinkIcon>📚</NavLinkIcon>
              {sidebarLabels.links.history}
            </NavLink>
            <NavLink to="/statistics" active={isActive('/statistics')} onClick={() => setSidebarOpen(false)}>
              <NavLinkIcon>📈</NavLinkIcon>
              {sidebarLabels.links.statistics}
            </NavLink>
            {hasFeature(user, 'distressThermometer') && (
              <NavLink to="/distress" active={isActive('/distress')} onClick={() => setSidebarOpen(false)}>
                <NavLinkIcon>🌡️</NavLinkIcon>
                {sidebarLabels.links.distress}
              </NavLink>
            )}
            {hasFeature(user, 'medicationReminders') && (
              <NavLink to="/medication" active={isActive('/medication')} onClick={() => setSidebarOpen(false)}>
                <NavLinkIcon>💊</NavLinkIcon>
                {sidebarLabels.links.medication}
                {unreadMedicationCount > 0 && (
                  <MedicationBadge>{unreadMedicationCount > 99 ? '99+' : unreadMedicationCount}</MedicationBadge>
                )}
              </NavLink>
            )}
          </NavLinks>
        </SidebarContent>

        <UserSection>
          {user && (
            <>
              <UserProfile to="/profile" onClick={() => setSidebarOpen(false)}>
                <UserAvatar>{getInitials()}</UserAvatar>
                <UserInfo>
                  <UserName>
                    {translatedFirstName || user.firstName} {translatedLastName || user.lastName}
                  </UserName>
                  <UserLabel>{sidebarLabels.user.profile}</UserLabel>
                </UserInfo>
              </UserProfile>
              <LogoutButton onClick={handleLogout}>
                🚪 {sidebarLabels.actions.logout}
              </LogoutButton>
            </>
          )}
        </UserSection>
      </SidebarContainer>
    </>
  );
};

export default Navbar;